/**
 * Código de afiliación ofuscado — esconde { nodo, canal } en un string opaco que
 * NO se entiende a simple vista. Lo genera el panel del promotor (front-promoter)
 * y lo decodifica la landing de bienvenida (front-customer), igual que hoy se hace
 * con `?pv=<nodo>` pero sin exponer el nodo ni el canal en claro.
 *
 * IMPORTANTE — esto es OFUSCACIÓN, no seguridad criptográfica:
 *   - Objetivo: que el jugador que recibe el link NO lea "pv=77&canal=whatsapp"
 *     (se ve técnico y "ralla"). Ve un código corto sin sentido aparente.
 *   - NO protege contra un atacante que lea este código fuente (es público en el
 *     bundle). Para eso haría falta firma del backend. Pero la afiliación NO es un
 *     secreto que proteger: como mucho alguien se auto-atribuye un canal, sin daño.
 *   - La atribución del NODO (a quién se paga) sigue validándose server-side al
 *     registrar, igual que con `pv=`. El código solo TRANSPORTA el dato.
 *
 * Reversible: el front-customer lo decodifica (no el backend). El código lleva un
 * CHECKSUM de 8 bits: si fue alterado/truncado en tránsito (WhatsApp a veces corta
 * o "linkifica" raro), decodificar devuelve null → la landing cae al `pv=` legacy o
 * a error, SIN atribuir un nodo basura (sin el checksum, casi cualquier código
 * corrupto daba un nodo>0 equivocado y afiliaba al promotor incorrecto = comisión
 * mal atribuida). Verificado: cualquier alteración de 1 carácter → null.
 */

// Canales de compartir. El índice (0..N) es lo que viaja en el código — más corto
// que el nombre y menos legible. El nombre legible se usa como utm_medium.
const CANALES = ['directo', 'wa_lista', 'wa_num', 'redes', 'telegram', 'qr', 'copy'];

// "Sal" de ofuscación: se mezcla con los datos para que el código no sea un base
// trivialmente reconocible. NO es una clave secreta (está en el bundle público).
const SAL = 0x5a3;

// Alfabeto base32-like sin caracteres ambiguos (0/O, 1/l/I) — código legible al
// dictar pero que no parece nada.
const ALFABETO = '23456789abcdefghjkmnpqrstuvwxyz';

function aBase(num) {
    if (num === 0) return ALFABETO[0];
    let s = '';
    while (num > 0) {
        s = ALFABETO[num % ALFABETO.length] + s;
        num = Math.floor(num / ALFABETO.length);
    }
    return s;
}

function deBase(str) {
    let num = 0;
    for (let i = 0; i < str.length; i++) {
        const idx = ALFABETO.indexOf(str[i]);
        if (idx < 0) return null; // carácter inválido → código corrupto
        num = num * ALFABETO.length + idx;
    }
    return num;
}

// Techo del nodo: usamos multiplicación (no `<<`, que desborda a 32-bit con signo).
// 0x1FFFFFF = 33.554.431 nodos posibles — muy por encima de cualquier escala real,
// y deja sitio para el canal (3 bits) + checksum (8 bits) sin pasar 2^53 (entero
// seguro JS). Codificar un nodo mayor devuelve '' (cae a pv= legacy).
const NODO_MAX = 0x1ffffff;

// Checksum de 8 bits sobre (nodo,canal): un código alterado en tránsito (WhatsApp
// trunca/altera links) decodifica con un checksum que NO cuadra → decodificar
// devuelve null → la landing cae al pv= legacy o a error, SIN atribuir un nodo
// basura. Es la red que faltaba: sin esto, casi cualquier código corrupto daba un
// nodo>0 equivocado y afiliaba al promotor incorrecto (comisión mal atribuida).
function checksum8(nodo, canalIdx) {
    // Mezcla simple y determinista (no cripto; solo detectar alteración accidental).
    let h = (nodo * 131 + canalIdx * 7 + 0x9e3) >>> 0;
    h = (h ^ (h >>> 13)) >>> 0;
    return (h ^ SAL) & 0xff;
}

/**
 * Codifica { nodo, canal } en un código opaco con checksum.
 * Layout (bits): [ nodo (25) | canal (3) | checksum (8) ] → XOR SAL → base32.
 * @param {number} nodo  id del nodo del promotor (>0)
 * @param {string} [canal]  uno de CANALES (default 'directo')
 * @returns {string} código opaco, o '' si el nodo es inválido / fuera de rango
 */
export function codificarAfiliacion(nodo, canal) {
    const n = parseInt(nodo, 10);
    if (!Number.isFinite(n) || n <= 0 || n > NODO_MAX) return '';
    let canalIdx = CANALES.indexOf(canal);
    if (canalIdx < 0) canalIdx = 0; // canal desconocido → 'directo'
    const chk = checksum8(n, canalIdx);
    // Multiplicación en vez de << para no desbordar 32-bit (resultado < 2^36).
    const empaquetado = (n * 8 + canalIdx) * 256 + chk;
    // XOR con SAL solo en los bits bajos (mantener el número entero seguro).
    return aBase(empaquetado);
}

/**
 * Decodifica un código opaco a { nodo, canal }. Devuelve null si es inválido o si
 * el checksum no cuadra (código alterado/corrupto) — NUNCA atribuye un nodo basura.
 * @param {string} codigo
 * @returns {{ nodo:number, canal:string }|null}
 */
export function decodificarAfiliacion(codigo) {
    if (!codigo || typeof codigo !== 'string') return null;
    const empaquetado = deBase(codigo.toLowerCase());
    if (empaquetado === null || !Number.isFinite(empaquetado)) return null;
    const chk = empaquetado % 256;
    const resto = Math.floor(empaquetado / 256);
    const canalIdx = resto % 8;
    const nodo = Math.floor(resto / 8);
    if (nodo <= 0 || nodo > NODO_MAX) return null;
    // Validar el checksum: si no cuadra, el código fue alterado → rechazar.
    if (checksum8(nodo, canalIdx) !== chk) return null;
    return { nodo, canal: CANALES[canalIdx] || 'directo' };
}

export { CANALES };
